import i18next, { type TFunction } from "i18next"

import type {
  EngineSetting,
  EngineSettings,
  SearchOptions,
  SearchSettings,
} from "@/api/filth-settings-api"

export const SEARCH_FIELDS = [
  {
    group: "adaptive_depth",
    fields: ["enabled", "ratio", "floor", "ceiling"],
  },
  {
    group: "multi_search",
    fields: ["enabled", "max_searches", "time_budget_seconds", "max_tokens"],
  },
  { group: "entity_arm", fields: ["enabled", "weight"] },
]
export const ENGINE_BOUNDS: Partial<Record<string, [number, number, boolean]>> = {
  "crawl.section_context_concurrency": [1, 8, true],
  "serving.context_entity_weight": [0, 1, false],
  "serving.context_entity_max_candidates": [1, 5000, true],
}
export function validateSearchDraft(
  options: SearchOptions,
  settings: SearchSettings,
  t: TFunction = i18next.t
): string | null {
  for (const { group, fields } of SEARCH_FIELDS) {
    for (const field of fields) {
      const value = options[group]?.[field]
      if (value == null) continue
      if (field === "enabled") {
        if (typeof value !== "boolean")
          return t("adminDashboard.filthSettings.booleanError", {
            field: t(`adminDashboard.filthSettings.fields.${field}`),
          })
      } else {
        const bounds = settings.bounds[group]?.[field]
        if (!bounds) return t("adminDashboard.filthSettings.unsupported")
        const [min, max, integer] = bounds
        if (
          typeof value !== "number" ||
          !Number.isFinite(value) ||
          value < min ||
          value > max ||
          (integer && !Number.isInteger(value))
        ) {
          return t("adminDashboard.filthSettings.numberError", {
            field: t(`adminDashboard.filthSettings.fields.${field}`),
            kind: t(
              integer
                ? "adminDashboard.filthSettings.integer"
                : "adminDashboard.filthSettings.number"
            ),
            min,
            max,
          })
        }
      }
    }
  }
  const floor = options.adaptive_depth?.floor ?? settings.inherited.adaptive_depth?.floor
  const ceiling = options.adaptive_depth?.ceiling ?? settings.inherited.adaptive_depth?.ceiling
  return Number(floor) > Number(ceiling) ? t("adminDashboard.filthSettings.floorError") : null
}
export function engineChanges(
  settings: EngineSetting[],
  draft: Record<string, string | number>
): Record<string, string | number> {
  return Object.fromEntries(
    settings
      .filter((item) => draft[item.key] !== item.value)
      .map((item) => [item.key, draft[item.key]])
  )
}
export function validateEngineDraft(
  changes: Record<string, string | number>,
  t: TFunction = i18next.t
): string | null {
  for (const [key, value] of Object.entries(changes)) {
    const bounds = ENGINE_BOUNDS[key]
    if (bounds) {
      const [min, max, integer] = bounds
      if (
        typeof value !== "number" ||
        !Number.isFinite(value) ||
        value < min ||
        value > max ||
        (integer && !Number.isInteger(value))
      )
        return t("adminDashboard.filthSettings.numberError", {
          field: t(`adminDashboard.filthSettings.fields.${key}`),
          kind: t(
            integer ? "adminDashboard.filthSettings.integer" : "adminDashboard.filthSettings.number"
          ),
          min,
          max,
        })
    } else if (!["off", "on"].includes(String(value)))
      return t("adminDashboard.filthSettings.modeError", {
        field: t(`adminDashboard.filthSettings.fields.${key}`),
      })
  }
  return null
}
export const ENGINE_ERROR_CODES = [
  "invalid_value",
  "unknown_key",
  "out_of_bounds",
  "kill_switch_active",
  "stale_revision",
  "cost_confirmation_required",
  "permission_denied",
  "estimate_id_required",
  "bad_request",
  "credential_error",
  "admin_credential_missing",
  "unsupported",
  "upstream_unavailable",
] as const
export const ENGINE_KEYS = [
  "crawl.section_path_prefix",
  "crawl.section_spreadsheet_rows",
  "crawl.section_context_notes",
  "crawl.section_context_concurrency",
  "serving.context_entity_arm",
  "serving.context_entity_weight",
  "serving.context_entity_max_candidates",
]
export function engineErrorMessage(error: unknown, t: TFunction = i18next.t): string {
  const data = (
    error as { response?: { data?: { error?: unknown; key?: unknown; result?: unknown } } } | null
  )?.response?.data
  if (data?.result === "applied_unverified")
    return t("adminDashboard.filthSettings.appliedUnverified")
  const code = ENGINE_ERROR_CODES.find((code) => code === data?.error) ?? "upstream_unavailable"
  const message = t(`adminDashboard.filthSettings.errors.${code}`)
  return typeof data?.key === "string" && ENGINE_KEYS.includes(data.key)
    ? t("adminDashboard.filthSettings.errorField", {
        message,
        field: t(`adminDashboard.filthSettings.fields.${data.key}`),
      })
    : message
}

export type CostConfirmation = {
  cost_estimate: Record<string, string | number>
  estimate_id: string
  expires_at: number
}
export function costConfirmation(error: unknown): CostConfirmation | null {
  const response = (error as { response?: { status?: number; data?: Partial<CostConfirmation> } })
    .response
  const data = response?.data
  if (
    response?.status !== 412 ||
    !data ||
    typeof data.estimate_id !== "string" ||
    !/^[A-Za-z0-9_-]{16,128}$/.test(data.estimate_id) ||
    typeof data.expires_at !== "number" ||
    !Number.isFinite(data.expires_at) ||
    !data.cost_estimate ||
    typeof data.cost_estimate !== "object" ||
    Array.isArray(data.cost_estimate)
  )
    return null
  return {
    cost_estimate: data.cost_estimate,
    estimate_id: data.estimate_id,
    expires_at: data.expires_at,
  }
}

export function searchEnabledChoice(value: boolean | number | null): string {
  if (value == null) return "inherit"
  return value ? "on" : "off"
}
export function numericInputValue(value: boolean | number | null): string {
  if (value == null || Number.isNaN(value)) return ""
  return String(value)
}
export function parseSearchNumber(value: string, badInput: boolean): number | null {
  if (badInput) return NaN
  return value === "" ? null : Number(value)
}
export function displaySearchValue(
  value: boolean | number | null | undefined,
  fallback = ""
): string {
  return value == null ? fallback : String(value)
}

export function engineBoundsLabel(bounds: EngineSetting["bounds"], t: TFunction): string {
  return "allowed" in bounds
    ? t("adminDashboard.filthSettings.allowedValues", {
        values: bounds.allowed
          .map((value) => t(`adminDashboard.filthSettings.${value}`))
          .join(", "),
      })
    : t("adminDashboard.filthSettings.allowedRange", { min: bounds.min, max: bounds.max })
}

export type SearchSettingsControls = {
  save: () => Promise<boolean>
  reload: () => Promise<void>
}

export function engineEntityValue(
  settings: EngineSettings | null | undefined,
  field: string
): boolean | number | null {
  const item = settings?.settings.find(
    (item) => item.key === `serving.context_entity_${field === "enabled" ? "arm" : "weight"}`
  )
  if (!item) return null
  return field === "enabled" ? item.value === "on" : Number(item.value)
}
